<template>
  <v-dialog @keydown.esc="isOpen=false" v-model="isOpen" persistent max-width="800px">
    <v-card>
      <v-btn
        dark
        fab
        x-small
        outlined
        color="primary"
        class="mr-3 mt-3"
        style="float: right"
        @click="closeDialog"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
      <v-card-title>
        <span class="headline">{{ product && product.product ? product.product.name : '' }}</span>
      </v-card-title>
      <v-divider />
      <v-card-text class="mt-5">
        <v-data-table
          :headers="table.headers"
          :items="table.data"
          :server-items-length="table.total"
          :items-per-page="table.total"
          class="elevation-1"
          :options="table.options"
          @update:options="updateTable"
          :loading="table.loading"
          :footer-props="{'items-per-page-options': [25, 50]}"
        >
          <template v-slot:top>
            <div class="d-flex align-center pa-2">
              <div class="mr-1">
                <span>Filtros:</span>
              </div>
              <div class="mr-2">
                <v-menu
                  ref="datePicker"
                  v-model="datePicker"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on }">
                    <v-text-field
                      v-model="range"
                      label="Rango de fechas"
                      prepend-icon="mdi-calendar"
                      readonly
                      hide-details
                      dense
                      v-on="on"
                    ></v-text-field>
                  </template>
                  <v-date-picker
                    v-model="dates"
                    :range="true"
                    first-day-of-week="1"
                    no-title
                  >
                    <v-btn text color="primary" @click="resetDate">Borrar</v-btn>
                    <v-spacer/>
                    <v-btn text color="grey" @click="datePicker = false">Cancel</v-btn>
                    <v-btn :disabled="dates.length < 2" color="primary" @click="filterByDate">OK</v-btn>
                  </v-date-picker>
                </v-menu>
              </div>
              <div class="mt-1 pa-1">
                <v-text-field
                  hide-details
                  dense
                  rounded
                  outlined
                  label="Buscar uuid"
                  append-icon="mdi-magnify"
                  v-model="search"
                />
              </div>
            </div>
          </template>
          <template v-slot:item.status="{ item }">
            <span :class="`${statusColors(item.status)}--text`">
            {{ statusNames(item.status) }}
            </span>
          </template>
          <template v-slot:item.createdAt="{ item }">
            {{ convertToDateString(item.createdAt) }}
          </template>
          <template v-slot:item.price="{ item }">
            {{ getPrice(item.products) }}
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>

    <v-overlay :value="overlay" z-index="99999">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import { debounce } from 'lodash'

export default {
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      overlay: false,
      isOpen: false,
      datePicker: false,
      dates: [
        this.$moment().startOf('month').format('YYYY-MM-DD'),
        this.$moment().endOf('month').format('YYYY-MM-DD')
      ],
      table: {
        loading: true,
        headers: [
          { text: 'UUID', value: 'uuid', sortable: false },
          { text: 'Fecha emisón', value: 'createdAt', sortable: false },
          { text: 'Estatus', value: 'status', sortable: false },
          { text: 'Precio variante', value: 'price', sortable: false }
        ],
        data: [],
        total: 0,
        options: {
          groupBy: [],
          groupDesc: [],
          itemsPerPage: 25,
          multiSort: false,
          mustSort: false,
          page: 1,
          sortBy: [''],
          sortDesc: []
        }
      },
      search: ''
    }
  },
  computed: {
    range () {
      return this.dates.length < 2 ? '' : `${this.dates[0]} / ${this.dates[1]}`
    }
  },
  watch: {
    'search': debounce(async function (val) {
      if (val.length >= 2) {
        await this.load({ skip: 0, uuid: `/${val}/i`, product: this.product.product._id })
      } else {
        await this.load()
      }
    }, 600),
    product: async function (value) {
      if (value && value._id) {
        await this.load()
      }
    }
  },
  methods: {
    getPrice (products) {
      const price = products.find(x => x.product === this.product.product._id)
      return `$ ${price.amountWithTax}`
    },
    async resetDate () {
      this.dates = [
        this.$moment().startOf('month').format('YYYY-MM-DD'),
        this.$moment().endOf('month').format('YYYY-MM-DD')
      ]
      await this.load()
      this.datePicker = false
    },
    async filterByDate () {
      await this.load()
      this.datePicker = false
    },
    convertToDateString (date) {
      const d = new Date(date)
      return d.toLocaleDateString('es-MX')
    },
    statusNames (status) {
      return {
        sent: 'Nueva orden',
        confirmed: 'Confirmada',
        canceled: 'Cancelada',
        invoiced: 'Facturada',
        invalid: 'Factura no coincide',
        received: 'Entregada',
        pending_payment: 'Pendiente de pago',
        paid: 'Cobrada'
      }[status]
    },
    statusColors (status) {
      return {
        sent: 'grey',
        confirmed: 'info',
        canceled: 'red',
        invoiced: 'info',
        invalid: 'red',
        invalid_invoice: 'red',
        received: 'primary',
        pending_payment: 'primary',
        paid: 'primary'
      }[status]
    },
    updateTable: async function (data) {
      const params = {
        limit: data.itemsPerPage,
        skip: (data.itemsPerPage * (data.page - 1)),
        product: this.product.product._id
      }

      await this.load(params)
    },
    async load (params = null) {
      params = params || {
        limit: this.table.options.itemsPerPage,
        skip: (this.table.options.itemsPerPage * (this.table.options.page - 1)),
        product: this.product.product._id
      }

      if (this.dates.length) {
        this.dates.sort((a, b) => {
          if (a < b) return -1
          if (a > b) return 1
          return 0
        })
        params['createdAt>'] = moment(this.dates[0]).startOf('day').toISOString()
        params['createdAt<'] = moment(this.dates[1]).endOf('day').toISOString()
      }

      if (this.search) params.uuid = `/${this.search}/i`

      this.table.loading = true
      try {
        const response = await this.$api.get('/v1/catalog/products/find-orders', params)
        this.table.data = response.data
        this.table.total = response.total
      } catch (error) {
        this.$toasted.show(error.message, { duration: 3000 })
      }
      this.table.loading = false
    },
    closeDialog () {
      this.isOpen = false
      this.dates = [
        this.$moment().startOf('month').format('YYYY-MM-DD'),
        this.$moment().endOf('month').format('YYYY-MM-DD')
      ]
      this.table.data = []
      this.table.total = 0
      this.$emit('close', false)
    },
    open () {
      this.isOpen = true
    }
  }
}
</script>
